import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  FC,
  CSSProperties,
} from 'react';
import {
  Search,
  Tag,
  Ticket,
  Copy,
  Check,
  ExternalLink,
  MessageCircle,
  Clock,
  Sparkles,
  ArrowRight,
  Info,
} from 'lucide-react';
import {
  PublicShowcaseData,
  PublicShowcaseOfferItem,
  fetchPublicShowcase,
} from '../../api/showcase';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { ShowcaseOfferDrawer } from './ShowcaseOfferDrawer';

const PAGE_SIZE = 20;

export interface PublicShowcaseViewProps {
  tenantSlug: string;
  initialData?: PublicShowcaseData | null;
}

export const PublicShowcaseView: FC<PublicShowcaseViewProps> = ({
  tenantSlug,
  initialData = null,
}) => {
  const [data, setData] = useState<PublicShowcaseData | null>(initialData);
  const [offersList, setOffersList] = useState<PublicShowcaseOfferItem[]>(
    initialData?.offers || []
  );
  const [loading, setLoading] = useState<boolean>(!initialData);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(
    initialData
      ? (initialData.offers?.length || 0) < (initialData.total_count || 0)
      : true
  );
  const [error, setError] = useState<string | null>(null);

  // Filtros locais reativos
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [copiedCouponId, setCopiedCouponId] = useState<string | null>(null);

  // Estado da aba lateral (Drawer) de detalhes da oferta
  const [selectedOffer, setSelectedOffer] =
    useState<PublicShowcaseOfferItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  // Ref sentinela para o Scroll Infinito via IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // 1. Carrega dados iniciais da vitrine pública do tenant
  useEffect(() => {
    if (initialData) {
      setOffersList(initialData.offers || []);
      setHasMore(
        (initialData.offers?.length || 0) < (initialData.total_count || 0)
      );
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchPublicShowcase(tenantSlug, { limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        if (isMounted) {
          setData(res);
          setOffersList(res.offers || []);
          setHasMore((res.offers?.length || 0) < (res.total_count || 0));
          setLoading(false);

          // Inicializa pixels de mensuração no client
          if (res.meta_pixel_id && typeof window !== 'undefined') {
            initMetaPixel(res.meta_pixel_id);
          }
          if (res.ga4_measurement_id && typeof window !== 'undefined') {
            initGA4(res.ga4_measurement_id);
          }
        }
      })
      .catch((err: unknown) => {
        if (isMounted) {
          const msg =
            err instanceof Error
              ? err.message
              : 'Não foi possível carregar a vitrine de ofertas.';
          setError(msg);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [tenantSlug, initialData]);

  // 2. Busca de novos lotes de ofertas para Scroll Infinito
  const loadMoreOffers = useCallback(async () => {
    if (loadingMore || !hasMore || !data) return;

    setLoadingMore(true);
    try {
      const nextOffset = offersList.length;
      const res = await fetchPublicShowcase(tenantSlug, {
        limit: PAGE_SIZE,
        offset: nextOffset,
      });

      if (res.offers && res.offers.length > 0) {
        setOffersList((prev) => {
          const existingIds = new Set(prev.map((o) => o.id));
          const newItems = res.offers.filter((o) => !existingIds.has(o.id));
          const updated = [...prev, ...newItems];
          if (updated.length >= res.total_count || newItems.length === 0) {
            setHasMore(false);
          }
          return updated;
        });
      } else {
        setHasMore(false);
      }
    } catch {
      // Ignora erro suave no scroll infinito
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, data, offersList.length, tenantSlug]);

  // 3. Observer para disparo de scroll infinito ao alcançar a sentinela
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreOffers();
        }
      },
      { rootMargin: '300px' }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadMoreOffers]);

  // 4. Sincronização da URL com o Drawer de Detalhes da Oferta (?offer=:id ou :shortCode)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const offerParam = urlParams.get('offer');

    if (offerParam && offersList.length > 0 && !isDrawerOpen) {
      const found = offersList.find(
        (o) => o.id === offerParam || o.short_code === offerParam
      );
      if (found) {
        setSelectedOffer(found);
        setIsDrawerOpen(true);
      }
    }
  }, [offersList, isDrawerOpen]);

  const handleOpenOfferDetails = (offer: PublicShowcaseOfferItem) => {
    setSelectedOffer(offer);
    setIsDrawerOpen(true);

    if (typeof window !== 'undefined' && window.history?.pushState) {
      const url = new URL(window.location.href);
      url.searchParams.set('offer', offer.short_code || offer.id);
      window.history.pushState({}, '', url.toString());
    }
  };

  const handleCloseOfferDetails = () => {
    setIsDrawerOpen(false);

    if (typeof window !== 'undefined' && window.history?.pushState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('offer');
      window.history.pushState({}, '', url.toString());
    }
  };

  // 5. Inicializador dinâmico de Meta Pixel
  const initMetaPixel = (pixelId: string) => {
    try {
      const win = window as unknown as { fbq?: (...args: unknown[]) => void };
      if (!win.fbq) {
        /* eslint-disable */
        (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod
              ? n.callMethod.apply(n, arguments)
              : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = !0;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(
          window,
          document,
          'script',
          'https://connect.facebook.net/en_US/fbevents.js'
        );
        /* eslint-enable */
      }
      if (win.fbq) {
        win.fbq('init', pixelId);
        win.fbq('track', 'PageView');
      }
    } catch {
      // Ignora erro
    }
  };

  // 6. Inicializador dinâmico de Google Analytics 4 (GA4)
  const initGA4 = (measurementId: string) => {
    try {
      const win = window as unknown as {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
      };
      if (!win.gtag) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);

        win.dataLayer = win.dataLayer || [];
        win.gtag = function () {
          win.dataLayer?.push(arguments);
        };
        win.gtag('js', new Date());
        win.gtag('config', measurementId);
      }
    } catch {
      // Ignora erro
    }
  };

  // 7. Copiar Cupom em 1 clique
  const handleCopyCoupon = (couponCode: string, offerId: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(couponCode);
      setCopiedCouponId(offerId);
      setTimeout(() => setCopiedCouponId(null), 2500);
    }
  };

  // 8. Filtro Reativo em Memória
  const filteredOffers = useMemo(() => {
    let list = offersList;

    if (selectedCategory && selectedCategory !== 'all') {
      const cleanCat = selectedCategory.toLowerCase();
      list = list.filter((o) =>
        o.categories.some((c) => c.toLowerCase() === cleanCat)
      );
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      list = list.filter(
        (o) =>
          o.title.toLowerCase().includes(term) ||
          (o.coupon_code && o.coupon_code.toLowerCase().includes(term)) ||
          o.marketplace.toLowerCase().includes(term)
      );
    }

    return list;
  }, [offersList, selectedCategory, searchTerm]);

  // Renderização de Estado de Carregamento
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas p-4">
        <div className="flex flex-col items-center space-y-3 text-center">
          <Spinner size="lg" color="brand" />
          <p className="text-xs font-medium text-content-muted">
            Carregando vitrine de promoções...
          </p>
        </div>
      </div>
    );
  }

  // Renderização de Estado de Erro
  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas p-4">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-surface-border bg-surface-panel p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-400">
            <Tag className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-content-main">
              Vitrine não disponível
            </h2>
            <p className="text-xs leading-relaxed text-content-muted">
              {error || 'Esta vitrine está temporariamente indisponível.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Injeção de variáveis CSS de branding do Tenant
  const dynamicBrandStyle: CSSProperties = {
    ['--brand-custom' as string]: data.primary_color,
    ['--brand-secondary-custom' as string]: data.secondary_color,
  };

  const categories = data.available_categories || [];
  const totalCount = data.total_count || 0;
  const isFiltering = searchTerm.trim() !== '' || selectedCategory !== 'all';

  return (
    <div
      style={dynamicBrandStyle}
      className="flex min-h-screen flex-col justify-between bg-surface-canvas font-sans text-content-main antialiased selection:bg-brand-500 selection:text-white"
    >
      {/* 1. Header do Canal com Identidade Visual & CTA Grupo VIP */}
      <header className="border-b border-surface-border bg-surface-panel">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-6 sm:flex-row sm:px-6">
          <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:text-left">
            {data.logo_url ? (
              <img
                src={data.logo_url}
                alt={data.public_title}
                className="h-12 max-w-[160px] rounded-xl object-contain sm:h-14"
              />
            ) : (
              <div
                className="flex h-12 w-12 items-center justify-center rounded-2xl text-lg font-bold text-white shadow-sm sm:h-14 sm:w-14"
                style={{ backgroundColor: data.primary_color }}
              >
                {data.public_title.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="space-y-0.5">
              <div className="flex items-center justify-center gap-2 sm:justify-start">
                <h1 className="text-base font-extrabold tracking-tight text-content-main sm:text-lg">
                  {data.public_title}
                </h1>
                <Badge variant="online" withDot pulse className="text-micro">
                  Vitrine Oficial
                </Badge>
              </div>
              <p className="max-w-md text-xs leading-relaxed text-content-muted">
                {data.slogan ||
                  'As melhores ofertas e promoções da internet em tempo real.'}
              </p>
            </div>
          </div>

          {/* Botão de Destaque para Entrada no Grupo VIP */}
          {data.vip_group_invite_link && (
            <div className="flex-shrink-0">
              <a
                href={data.vip_group_invite_link}
                target="_blank"
                rel="noreferrer"
                style={{ backgroundColor: data.primary_color }}
                className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold text-white shadow-sm transition-all duration-150 hover:opacity-95 active:scale-[0.98] sm:text-sm"
              >
                <MessageCircle className="h-4 w-4 fill-white stroke-none" />
                <span>Entrar no Grupo VIP</span>
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </header>

      {/* 2. Barra de Busca & Filtros por Categoria */}
      <section className="bg-surface-subtle/50 border-b border-surface-border py-4">
        <div className="mx-auto max-w-6xl space-y-3 px-4 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Campo de Busca Instantânea */}
            <div className="w-full sm:max-w-md">
              <Input
                placeholder="Buscar por produto, marca ou cupom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4 text-content-muted" />}
              />
            </div>

            {/* Contador de Ofertas: Exibe o Total Real Cadastrado no Canal */}
            <div className="flex items-center gap-1.5 text-xs text-content-muted sm:ml-auto">
              <Sparkles className="h-3.5 w-3.5 text-brand-500" />
              <span>
                {isFiltering ? (
                  <>
                    <strong className="text-content-main">
                      {filteredOffers.length}
                    </strong>{' '}
                    de{' '}
                    <strong className="text-content-main">{totalCount}</strong>{' '}
                    {totalCount === 1 ? 'oferta no canal' : 'ofertas no canal'}
                  </>
                ) : (
                  <>
                    <strong className="text-content-main">{totalCount}</strong>{' '}
                    {totalCount === 1
                      ? 'oferta disponível'
                      : 'ofertas disponíveis'}
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Pílulas de Categorias de Nicho */}
          {categories.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
              <button
                type="button"
                onClick={() => setSelectedCategory('all')}
                className={`cursor-pointer whitespace-nowrap rounded-lg border px-3 py-1 font-medium transition-colors ${
                  selectedCategory === 'all'
                    ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400'
                    : 'border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                }`}
              >
                Todas ({totalCount})
              </button>
              {categories.map((cat) => {
                const isSelected =
                  selectedCategory.toLowerCase() === cat.toLowerCase();
                const count = offersList.filter((o) =>
                  o.categories.some(
                    (c) => c.toLowerCase() === cat.toLowerCase()
                  )
                ).length;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`cursor-pointer whitespace-nowrap rounded-lg border px-3 py-1 font-medium transition-colors ${
                      isSelected
                        ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400'
                        : 'border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                    }`}
                  >
                    {cat} {count > 0 ? `(${count})` : ''}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 3. Grid Responsivo de Ofertas & Scroll Infinito */}
      <main className="mx-auto my-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">
        {filteredOffers.length === 0 ? (
          <div className="space-y-3 rounded-3xl border border-surface-border bg-surface-panel p-10 text-center shadow-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-subtle text-content-muted">
              <Search className="h-6 w-6" />
            </div>
            <h3 className="text-sm font-semibold text-content-main">
              Nenhuma oferta encontrada
            </h3>
            <p className="mx-auto max-w-md text-xs leading-relaxed text-content-muted">
              {isFiltering
                ? 'Tente buscar com outros termos ou limpar os filtros de categoria selecionados.'
                : 'Fique de olho! Novas promoções serão publicadas em breve.'}
            </p>
            {isFiltering && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
              >
                Limpar Filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {filteredOffers.map((offer) => (
                <ShowcaseOfferCard
                  key={offer.id}
                  offer={offer}
                  primaryColor={data.primary_color}
                  isCopied={copiedCouponId === offer.id}
                  onCopyCoupon={() =>
                    offer.coupon_code &&
                    handleCopyCoupon(offer.coupon_code, offer.id)
                  }
                  onViewDetails={() => handleOpenOfferDetails(offer)}
                />
              ))}
            </div>

            {/* Sentinela de Scroll Infinito & Indicador de Carregamento */}
            {!isFiltering && hasMore && (
              <div
                ref={sentinelRef}
                className="flex flex-col items-center justify-center py-6 text-center"
              >
                {loadingMore && (
                  <div className="flex items-center gap-2 text-xs font-medium text-content-muted">
                    <Spinner size="sm" color="brand" />
                    <span>Carregando mais promoções...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* 4. Drawer de Detalhes da Oferta com Telemetria de Envio */}
      <ShowcaseOfferDrawer
        offer={selectedOffer}
        isOpen={isDrawerOpen}
        onClose={handleCloseOfferDetails}
        primaryColor={data.primary_color}
        isCopied={copiedCouponId === selectedOffer?.id}
        onCopyCoupon={() =>
          selectedOffer?.coupon_code &&
          handleCopyCoupon(selectedOffer.coupon_code, selectedOffer.id)
        }
      />

      {/* 5. Rodapé Co-branded & Links Institucionais */}
      <footer className="border-t border-surface-border bg-surface-panel py-6 text-center text-micro text-content-muted">
        <div className="mx-auto max-w-6xl space-y-2 px-4 sm:px-6">
          <p>
            © {new Date().getFullYear()} {data.public_title}. Todos os direitos
            reservados.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {data.privacy_policy_url && (
              <>
                <a
                  href={data.privacy_policy_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:text-content-main hover:underline"
                >
                  <span>Política de Privacidade</span>
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
                <span>•</span>
              </>
            )}
            <span className="font-semibold text-content-muted">
              Powered by TrafficForge
            </span>
          </div>
          <p className="text-content-muted/80 text-2xs">
            Os preços, estoques e condições promocionais são de responsabilidade
            das lojas parceiras e podem sofrer alterações sem aviso prévio. Este
            site não possui vínculo oficial com o WhatsApp ou Meta Inc.
          </p>
        </div>
      </footer>
    </div>
  );
};

interface ShowcaseOfferCardProps {
  offer: PublicShowcaseOfferItem;
  primaryColor: string;
  isCopied: boolean;
  onCopyCoupon: () => void;
  onViewDetails: () => void;
}

const ShowcaseOfferCard: FC<ShowcaseOfferCardProps> = ({
  offer,
  primaryColor,
  isCopied,
  onCopyCoupon,
  onViewDetails,
}) => {
  return (
    <div
      onClick={onViewDetails}
      className="group flex cursor-pointer flex-col justify-between overflow-hidden rounded-2xl border border-surface-border bg-surface-panel shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
    >
      {/* Imagem do Produto & Badges de Destaque */}
      <div className="relative aspect-square w-full overflow-hidden border-b border-surface-border bg-surface-subtle">
        {offer.image_url ? (
          <img
            src={offer.image_url}
            alt={offer.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-content-muted">
            {offer.is_coupon_only ? (
              <Ticket className="h-10 w-10 opacity-40" />
            ) : (
              <Tag className="h-10 w-10 opacity-40" />
            )}
          </div>
        )}

        {/* Loja Parceira no Topo Esquerdo */}
        <div className="absolute left-2.5 top-2.5">
          <Badge
            variant="neutral"
            className="font-mono text-micro font-bold uppercase shadow-sm"
          >
            {offer.marketplace}
          </Badge>
        </div>

        {/* Porcentagem de Economia no Topo Direito */}
        {offer.discount_percent && offer.discount_percent > 0 && (
          <div className="absolute right-2.5 top-2.5">
            <span
              style={{ backgroundColor: primaryColor }}
              className="rounded-lg px-2 py-0.5 text-xs font-extrabold text-white shadow-sm"
            >
              -{offer.discount_percent}%
            </span>
          </div>
        )}
      </div>

      {/* Corpo com Informações do Produto */}
      <div className="flex flex-1 flex-col justify-between p-4">
        <div className="space-y-2">
          {/* Título da Promoção */}
          <h3
            className="line-clamp-2 text-xs font-semibold leading-relaxed text-content-main group-hover:text-brand-600 dark:group-hover:text-brand-400"
            title={offer.title}
          >
            {offer.title}
          </h3>

          {/* Preços (De / Por) */}
          <div className="space-y-0.5 pt-1">
            {offer.original_price && offer.promotional_price && (
              <span className="block text-2xs text-content-muted line-through">
                R$ {offer.original_price.toFixed(2).replace('.', ',')}
              </span>
            )}
            {offer.promotional_price ? (
              <span className="block text-base font-extrabold text-content-main">
                R$ {offer.promotional_price.toFixed(2).replace('.', ',')}
              </span>
            ) : offer.is_coupon_only ? (
              <span className="block text-xs font-bold text-brand-600 dark:text-brand-400">
                Cupom de Desconto
              </span>
            ) : null}
          </div>

          {/* Bloco de Cupom com 1 Clique */}
          {offer.coupon_code && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="mt-2 flex items-center justify-between rounded-xl border border-dashed border-brand-500/40 bg-brand-500/5 p-2"
            >
              <div className="flex items-center gap-1.5 overflow-hidden">
                <Ticket className="h-3.5 w-3.5 flex-shrink-0 text-brand-500" />
                <span className="truncate font-mono text-xs font-bold text-content-main">
                  {offer.coupon_code}
                </span>
              </div>
              <button
                type="button"
                onClick={onCopyCoupon}
                className="shadow-xs flex cursor-pointer items-center gap-1 rounded-md bg-surface-panel px-2 py-1 text-micro font-semibold text-brand-600 transition-colors hover:bg-surface-subtle dark:text-brand-400"
                title="Copiar cupom de desconto"
              >
                {isCopied ? (
                  <>
                    <Check className="h-3 w-3 text-brand-500" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    <span>Copiar</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Etiqueta Amigável de Aviso de Idade */}
          {offer.is_age_warning && (
            <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5 text-2xs text-amber-700 dark:text-amber-400">
              <Clock className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
              <span className="leading-tight">
                Oferta publicada há alguns dias: o preço promocional ou estoque
                pode ter variado.
              </span>
            </div>
          )}
        </div>

        {/* Botão de Ação: Ver Detalhes / Pegar Promoção */}
        <div className="flex items-center gap-2 pt-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
            className="flex w-1/3 cursor-pointer items-center justify-center gap-1 rounded-xl border border-surface-border bg-surface-subtle py-2 text-micro font-semibold text-content-muted transition-colors hover:bg-surface-panel hover:text-content-main"
            title="Ver detalhes da promoção"
          >
            <Info className="h-3.5 w-3.5" />
            <span>Detalhes</span>
          </button>

          <a
            href={offer.tracked_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: primaryColor }}
            className="shadow-xs flex w-2/3 cursor-pointer items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-bold text-white transition-all duration-150 hover:opacity-95 active:scale-[0.99]"
          >
            <span>Pegar Oferta</span>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};
